import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { ClassRoomsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";

export interface UseClassRoomsGetPublicOptions {
  page?: number;
  pageSize?: number;
  /** When true, only active class-rooms (API filter). Default true. */
  active?: boolean;
  enabled?: boolean;
}

/**
 * Paginated class-rooms for the tenant - works with or without auth (same pattern as `useLocationGetPublic`).
 * Filter by `locationId` on the client, or add a dedicated backend endpoint later.
 */
export const useClassRoomsGetPublic = ({
  page = 1,
  pageSize = 1000,
  active = true,
  enabled = true,
}: UseClassRoomsGetPublicOptions = {}) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } = useQuery({
    queryKey: [QueryKeys.ClassRooms, tenantId, "public", page, pageSize, active],
    queryFn: () => {
      const params = {
        page,
        pageSize,
        active,
        xTenantID: tenantId,
      };
      if (accessToken) {
        const service = createAuthenticatedApi(ClassRoomsApi, accessToken);
        return service.apiClassRoomsScrollGet(params);
      }
      const service = new ClassRoomsApi(createOpenApiConfiguration());
      return service.apiClassRoomsScrollGet(params);
    },
    enabled: enabled && tenantId > 0,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
